import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
} from 'react';
import PropTypes from 'prop-types';
import { parseJwt } from './parseJwt';

const DEFAULT_STATE = {
  user: null,
  enabled: false,
  permissions: [],
};

export const UserAuthenticationContext = createContext(DEFAULT_STATE);

export function UserAuthenticationProvider({ children, service }) {
  const userAuthenticationReducer = (state, action) => {
    switch (action.type) {
      case 'SET_USER': {
        const userInfo = action.payload.user;
        const permissionToken = userInfo?.permission_token;
        const parseInfo = permissionToken && parseJwt(permissionToken);
        const permissionsList = parseInfo?.authorization?.permissions || [];
        const permissions =
          permissionsList.find((it) => it?.rsname === 'api') ||
          permissionsList[0];

        return {
          ...state,
          ...{
            user: action.payload.user,
            permissions: permissions?.scopes || userInfo?.permissions || [],
          },
        };
      }
      case 'RESET': {
        return {
          user: null,
        };
      }

      case 'SET': {
        return {
          ...state,
          ...action.payload,
        };
      }

      default:
        return action.payload;
    }
  };

  const [userAuthenticationState, dispatch] = useReducer(
    userAuthenticationReducer,
    DEFAULT_STATE
  );

  const getState = useCallback(() => userAuthenticationState, [
    userAuthenticationState,
  ]);

  const setUser = useCallback(
    user =>
      dispatch({
        type: 'SET_USER',
        payload: {
          user,
        },
      }),
    [dispatch]
  );

  const getUser = useCallback(() => userAuthenticationState.user, [
    userAuthenticationState,
  ]);

  const hasOneOfPerm = useCallback(
    (perms: string[]) => {
      const permissions = userAuthenticationState.permissions;

      if (!perms?.length) return true;
      if (!permissions?.length) return false;

      for (const perm of perms) {
        if (typeof perm === 'object') {
          // if perm is object, check all perm in object
          const list = Object.values(perm);
          const check = this.hasAllOfPerm(list);

          if (check) return true;
        } else if (permissions.includes(perm)) {
          return true;
        }
      }
    },
    [userAuthenticationState]
  );

  const reset = useCallback(
    () =>
      dispatch({
        type: 'RESET',
        payload: {},
      }),
    [dispatch]
  );

  const set = useCallback(
    payload =>
      dispatch({
        type: 'SET',
        payload,
      }),
    [dispatch]
  );

  /**
   * Sets the implementation of the UserAuthenticationService that can be used by extensions.
   *
   * @returns void
   */
  // TODO: should this be a useEffect or not?
  useEffect(() => {
    if (service) {
      service.setServiceImplementation({
        getState,
        setUser,
        getUser,
        hasOneOfPerm,
        reset,
        set,
      });
    }
  }, [getState, service, setUser, getUser, hasOneOfPerm, reset, set]);

  // TODO: This may not be correct, but I think we need to set the implementation for the service
  // immediately when this runs, since otherwise the authentication redirects will fail.
  // (useEffect only runs after the child components - in this case, routing logic - has failed)
  if (service) {
    service.setServiceImplementation({
      getState,
      setUser,
      getUser,
      hasOneOfPerm,
      reset,
      set,
    });
  }

  const api = {
    getState,
    setUser,
    getUser,
    hasOneOfPerm,
    getAuthorizationHeader: service.getAuthorizationHeader,
    handleUnauthenticated: service.handleUnauthenticated,
    reset,
    set,
  };

  return (
    <UserAuthenticationContext.Provider value={[userAuthenticationState, api]}>
      {children}
    </UserAuthenticationContext.Provider>
  );
}

export default UserAuthenticationProvider;

const UserAuthenticationConsumer = UserAuthenticationContext.Consumer;
export { UserAuthenticationConsumer };

UserAuthenticationProvider.propTypes = {
  children: PropTypes.any,
  service: PropTypes.shape({
    setServiceImplementation: PropTypes.func,
  }).isRequired,
};

export const useUserAuthentication = () =>
  useContext(UserAuthenticationContext);
